/**
 * Centralized configuration for all architecture pages.
 * Used by ArchitectureNavigation component to generate tabs.
 */

// Import API groups for dynamic children
import { getCustomerPortalApiGroups, getPublicApiGroups, getGroupEndpoints } from 'utils/architecture/apiEndpoints';

export const ARCHITECTURE_PAGES = [
  { 
    id: 'overview', 
    label: 'Architecture', 
    path: '/architecture', 
    translationKey: 'overview'
  },
  { 
    id: 'runtime', 
    label: 'Runtime', 
    path: '/architecture/runtime',
    translationKey: 'runtime'
  },
  { 
    id: 'gateway', 
    label: 'Gateway', 
    path: '/architecture/gateway',
    translationKey: 'gateway'
  },
  { 
    id: 'compute', 
    label: 'Compute', 
    path: '/architecture/compute',
    translationKey: 'compute'
  },
  { 
    id: 'database', 
    label: 'Database', 
    path: '/architecture/database',
    translationKey: 'database'
  },
  { 
    id: 'components', 
    label: 'Architecture Components', 
    path: '/architecture/components',
    translationKey: 'components'
  },
  { 
    id: 'processes', 
    label: 'Processes', 
    path: '/architecture/processes',
    translationKey: 'processes',
    children: [
      {
        id: 'customer-portal',
        label: 'Customer Portal APIs',
        path: '/architecture/processes/customer-portal',
        translationKey: 'customerPortal'
      },
      {
        id: 'public',
        label: 'Public APIs',
        path: '/architecture/processes/public',
        translationKey: 'public'
      }
    ]
  }
];

/**
 * Get all possible paths for an architecture page (including language prefixes and alt paths)
 */
export const getAllPathsForPage = (page) => {
  const paths = [page.path, ...(page.altPaths || [])];
  // Add language-prefixed versions
  const allPaths = [];
  paths.forEach(path => {
    allPaths.push(path);
    // Add /:lang/ prefix version
    allPaths.push(path.replace(/^\//, '/:lang/'));
  });
  return allPaths;
};

/**
 * Check if current path matches an architecture page
 * Returns the most specific match (child pages take precedence over parent pages)
 */
export const getCurrentArchitecturePage = (currentPath) => {
  // Remove hash and query parameters
  const pathWithoutHash = currentPath.split('#')[0].split('?')[0];
  
  // Remove language prefix if present (e.g., /de/architecture/... -> /architecture/...)
  const normalizedPath = pathWithoutHash.replace(/^\/[a-z]{2}(\/|$)/, '/');
  
  // Check for customer-portal group pages (e.g., /architecture/processes/customer-portal/resources)
  const customerPortalGroupMatch = normalizedPath.match(/^\/architecture\/processes\/customer-portal\/([^/]+)(?:\/(.+))?$/);
  if (customerPortalGroupMatch) {
    const groupId = customerPortalGroupMatch[1];
    const endpointId = customerPortalGroupMatch[2];
    
    // If there's an endpoint ID, this is an endpoint page
    if (endpointId) {
      try {
        const apiGroups = getCustomerPortalApiGroups();
        const group = apiGroups.find(g => g.id === groupId);
        if (group) {
          return {
            id: `${groupId}-${endpointId}`,
            label: endpointId,
            path: normalizedPath,
            translationKey: `customerPortal.${groupId}.${endpointId}`,
            parentId: groupId
          };
        }
      } catch (error) {
        console.error('Error loading customer portal API groups:', error);
      }
    } else {
      // This is a group page
      try {
        const apiGroups = getCustomerPortalApiGroups();
        const group = apiGroups.find(g => g.id === groupId);
        if (group) {
          return {
            id: groupId,
            label: group.name,
            path: normalizedPath,
            translationKey: `customerPortal.${groupId}`,
            parentId: 'customer-portal'
          };
        }
      } catch (error) {
        console.error('Error loading customer portal API groups:', error);
      }
    }
  }
  
  // Check for public API group pages (e.g., /architecture/processes/public/bot-operations)
  const publicApiGroupMatch = normalizedPath.match(/^\/architecture\/processes\/public\/([^/]+)(?:\/(.+))?$/);
  if (publicApiGroupMatch) {
    const groupId = publicApiGroupMatch[1];
    const endpointId = publicApiGroupMatch[2];
    
    // If there's an endpoint ID, this is an endpoint page
    if (endpointId) {
      try {
        const apiGroups = getPublicApiGroups();
        const group = apiGroups.find(g => g.id === groupId);
        if (group) {
          return {
            id: `${groupId}-${endpointId}`,
            label: endpointId,
            path: normalizedPath,
            translationKey: `public.${groupId}.${endpointId}`,
            parentId: groupId
          };
        }
      } catch (error) {
        console.error('Error loading public API groups:', error);
      }
    } else {
      // This is a group page
      try {
        const apiGroups = getPublicApiGroups();
        const group = apiGroups.find(g => g.id === groupId);
        if (group) {
          return {
            id: groupId,
            label: group.name,
            path: normalizedPath,
            translationKey: `public.${groupId}`,
            parentId: 'public'
          };
        }
      } catch (error) {
        console.error('Error loading public API groups:', error);
      }
    }
  }
  
  // First, check child pages (more specific matches first)
  for (const page of ARCHITECTURE_PAGES) {
    if (page.children) {
      for (const child of page.children) {
        if (normalizedPath === child.path || 
            normalizedPath === `${child.path}/` ||
            normalizedPath.startsWith(child.path + '/')) {
          return child;
        }
      }
    }
  }
  
  // Then try exact match for top-level pages
  for (const page of ARCHITECTURE_PAGES) {
    if (normalizedPath === page.path || 
        normalizedPath === `${page.path}/` ||
        (page.altPaths && page.altPaths.some(alt => normalizedPath === alt || normalizedPath === `${alt}/`))) {
      return page;
    }
  }
  
  // Finally, try startsWith match for nested routes (e.g., /architecture/processes matches /architecture/processes)
  for (const page of ARCHITECTURE_PAGES) {
    if (normalizedPath.startsWith(page.path + '/') || 
        normalizedPath === page.path ||
        (page.altPaths && page.altPaths.some(alt => normalizedPath.startsWith(alt + '/') || normalizedPath === alt))) {
      return page;
    }
  }
  
  return null;
};

/**
 * Get children of a page by ID
 * For customer-portal and public, dynamically generates children from API groups
 * For group pages, dynamically generates children from endpoints
 */
export const getChildrenOfPage = (pageId) => {
  const page = ARCHITECTURE_PAGES.find(p => p.id === pageId);
  
  // For customer-portal page, dynamically generate children from API groups
  if (pageId === 'customer-portal') {
    try {
      const apiGroups = getCustomerPortalApiGroups();
      return apiGroups.map(group => ({
        id: group.id,
        label: group.name,
        path: `/architecture/processes/customer-portal/${group.id}`,
        translationKey: `customerPortal.${group.id}`
      }));
    } catch (error) {
      console.error('Error loading customer portal API groups:', error);
      return [];
    }
  }
  
  // For public APIs page, dynamically generate children from API groups
  if (pageId === 'public') {
    try {
      const apiGroups = getPublicApiGroups();
      return apiGroups.map(group => ({
        id: group.id,
        label: group.name,
        path: `/architecture/processes/public/${group.id}`,
        translationKey: `public.${group.id}`
      }));
    } catch (error) {
      console.error('Error loading public API groups:', error);
      return [];
    }
  }
  
  // For customer-portal group pages, generate children from endpoints
  try {
    const apiGroups = getCustomerPortalApiGroups();
    const group = apiGroups.find(g => g.id === pageId);
    if (group) {
      const endpoints = getGroupEndpoints('customer', pageId);
      return endpoints.map(ep => {
        // Generate a readable label from the endpoint description or path
        let readableLabel = ep.description || ep.path.split('/').pop() || ep.endpointId;
        // Extract the main action/operation from description
        if (ep.description) {
          // Try to extract a short title from description (first few words)
          const words = ep.description.split(' ');
          if (words.length > 3) {
            readableLabel = words.slice(0, 3).join(' ');
          } else {
            readableLabel = ep.description;
          }
        } else {
          // Convert endpoint ID to readable format
          readableLabel = ep.endpointId
            .replace(/-/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
        }
        
        return {
          id: `${pageId}-${ep.endpointId}`,
          label: readableLabel,
          path: `/architecture/processes/customer-portal/${pageId}/${ep.endpointId}`,
          translationKey: `customerPortal.${pageId}.${ep.endpointId}`
        };
      });
    }
  } catch (error) {
    // Not a customer-portal group page, continue
  }
  
  // For public API group pages, generate children from endpoints
  try {
    const apiGroups = getPublicApiGroups();
    const group = apiGroups.find(g => g.id === pageId);
    if (group) {
      const endpoints = getGroupEndpoints('public', pageId);
      return endpoints.map(ep => {
        // Generate a readable label from the endpoint description or path
        let readableLabel = ep.description || ep.path.split('/').pop() || ep.endpointId;
        // Extract the main action/operation from description
        if (ep.description) {
          // Try to extract a short title from description (first few words)
          const words = ep.description.split(' ');
          if (words.length > 3) {
            readableLabel = words.slice(0, 3).join(' ');
          } else {
            readableLabel = ep.description;
          }
        } else {
          // Convert endpoint ID to readable format
          readableLabel = ep.endpointId
            .replace(/-/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
        }
        
        return {
          id: `${pageId}-${ep.endpointId}`,
          label: readableLabel,
          path: `/architecture/processes/public/${pageId}/${ep.endpointId}`,
          translationKey: `public.${pageId}.${ep.endpointId}`
        };
      });
    }
  } catch (error) {
    // Not a public API group page, continue
  }
  
  return page?.children || [];
};

/**
 * Get page by ID
 */
export const getArchitecturePageById = (pageId) => {
  return ARCHITECTURE_PAGES.find(p => p.id === pageId);
};

/**
 * Get parent page of a given page
 * Returns the parent page if the given page is a child, null otherwise
 */
export const getParentPage = (pageId) => {
  // Handle dynamic customer-portal and public API pages
  if (pageId && pageId.includes('-')) {
    // Check if it's an endpoint page (groupId-endpointId format)
    const parts = pageId.split('-');
    if (parts.length >= 2) {
      // Try customer-portal first
      try {
        const apiGroups = getCustomerPortalApiGroups();
        const group = apiGroups.find(g => g.id === parts[0]);
        if (group) {
          // This is a customer-portal endpoint page, parent is the group
          return {
            id: parts[0],
            label: group.name,
            path: `/architecture/processes/customer-portal/${parts[0]}`,
            translationKey: `customerPortal.${parts[0]}`,
            parentId: 'customer-portal'
          };
        }
      } catch (error) {
        console.error('Error loading customer portal API groups:', error);
      }
      
      // Try public APIs
      try {
        const apiGroups = getPublicApiGroups();
        const group = apiGroups.find(g => g.id === parts[0]);
        if (group) {
          // This is a public API endpoint page, parent is the group
          return {
            id: parts[0],
            label: group.name,
            path: `/architecture/processes/public/${parts[0]}`,
            translationKey: `public.${parts[0]}`,
            parentId: 'public'
          };
        }
      } catch (error) {
        console.error('Error loading public API groups:', error);
      }
    }
  }
  
  // Check if it's a customer-portal group page
  try {
    const apiGroups = getCustomerPortalApiGroups();
    const group = apiGroups.find(g => g.id === pageId);
    if (group) {
      // Parent is customer-portal
      return ARCHITECTURE_PAGES.find(p => p.id === 'processes')?.children?.find(c => c.id === 'customer-portal') || null;
    }
  } catch (error) {
    console.error('Error loading customer portal API groups:', error);
  }
  
  // Check if it's a public API group page
  try {
    const apiGroups = getPublicApiGroups();
    const group = apiGroups.find(g => g.id === pageId);
    if (group) {
      // Parent is public
      return ARCHITECTURE_PAGES.find(p => p.id === 'processes')?.children?.find(c => c.id === 'public') || null;
    }
  } catch (error) {
    console.error('Error loading public API groups:', error);
  }
  
  // Check if this page is a child of any parent
  for (const parent of ARCHITECTURE_PAGES) {
    if (parent.children) {
      const child = parent.children.find(c => c.id === pageId);
      if (child) {
        return parent;
      }
    }
  }
  return null;
};

/**
 * Get parent page from current path
 */
export const getParentPageFromPath = (currentPath) => {
  const currentPage = getCurrentArchitecturePage(currentPath);
  if (!currentPage) return null;
  
  return getParentPage(currentPage.id);
};

/**
 * Full page configuration with hierarchy
 */
export const ARCHITECTURE_PAGE_CONFIG = ARCHITECTURE_PAGES;

